/**
 * DICOM video (MPEG) transfer syntax detection.
 *
 * Mirrors backend `Shared.Services.VideoTransferSyntax`. These transfer syntaxes
 * encapsulate an MPEG-2 / MPEG-4 AVC (H.264) / HEVC (H.265) elementary stream in
 * Pixel Data — used by Video Endoscopic, Video Photographic and Video Microscopic
 * image objects. Such instances are NOT raster frames, so they are routed to the
 * dedicated <video> viewport instead of Cornerstone.
 *
 * UID list per DICOM PS3.5 Annex A. Each base UID also has a ".1" fragmentable
 * variant.
 */
export const VIDEO_TRANSFER_SYNTAXES = {
    MPEG2_MAIN_MAIN: '1.2.840.10008.1.2.4.100',
    MPEG2_MAIN_MAIN_FRAG: '1.2.840.10008.1.2.4.100.1',
    MPEG2_MAIN_HIGH: '1.2.840.10008.1.2.4.101',
    MPEG2_MAIN_HIGH_FRAG: '1.2.840.10008.1.2.4.101.1',
    MPEG4_AVC: '1.2.840.10008.1.2.4.102',
    MPEG4_AVC_FRAG: '1.2.840.10008.1.2.4.102.1',
    MPEG4_AVC_BD: '1.2.840.10008.1.2.4.103',
    MPEG4_AVC_BD_FRAG: '1.2.840.10008.1.2.4.103.1',
    MPEG4_AVC_2D: '1.2.840.10008.1.2.4.104',
    MPEG4_AVC_2D_FRAG: '1.2.840.10008.1.2.4.104.1',
    MPEG4_AVC_3D: '1.2.840.10008.1.2.4.105',
    MPEG4_AVC_3D_FRAG: '1.2.840.10008.1.2.4.105.1',
    MPEG4_AVC_STEREO: '1.2.840.10008.1.2.4.106',
    MPEG4_AVC_STEREO_FRAG: '1.2.840.10008.1.2.4.106.1',
    HEVC: '1.2.840.10008.1.2.4.107',
    HEVC_MAIN10: '1.2.840.10008.1.2.4.108',
};
const ALL_VIDEO_UIDS = new Set(Object.values(VIDEO_TRANSFER_SYNTAXES));
/** True if the transfer syntax UID encapsulates an MPEG video stream. */
export function isVideoTransferSyntax(transferSyntaxUid) {
    return !!transferSyntaxUid && ALL_VIDEO_UIDS.has(transferSyntaxUid);
}
/** Codec family for a video transfer syntax, or null if not a video syntax. */
export function getVideoCodec(transferSyntaxUid) {
    if (!transferSyntaxUid || !ALL_VIDEO_UIDS.has(transferSyntaxUid))
        return null;
    if (transferSyntaxUid === VIDEO_TRANSFER_SYNTAXES.HEVC ||
        transferSyntaxUid === VIDEO_TRANSFER_SYNTAXES.HEVC_MAIN10) {
        return 'hevc';
    }
    if (transferSyntaxUid.startsWith('1.2.840.10008.1.2.4.100') ||
        transferSyntaxUid.startsWith('1.2.840.10008.1.2.4.101')) {
        return 'mpeg2';
    }
    return 'h264';
}
/**
 * True when an image instance should be played as video. Prefers the backend
 * `isVideo` flag, falling back to inspecting the transfer syntax UID (covers
 * older API responses and the shared-viewer path).
 */
export function isVideoImage(image) {
    return image.isVideo === true || isVideoTransferSyntax(image.displayMetadata?.transferSyntaxUID);
}
/**
 * True when a series is an MPEG video series (all of its instances are video).
 *
 * The server-side `isVideo` flag is authoritative when present, and it MUST be:
 * on a summary-loaded study `images` is empty until that series is paged in, so
 * the instance-walk below would answer `false` for a genuine MPEG series and
 * route it into the Cornerstone raster pipeline, which cannot decode it.
 *
 * The fallback (`.every`, not `.some`, so one odd instance can't misroute a
 * normal series) covers the fat payloads that carry no series-level flag — the
 * share view and any older API response.
 */
export function isVideoSeries(series) {
    if (!series)
        return false;
    if (typeof series.isVideo === 'boolean')
        return series.isVideo;
    if (series.images.length === 0)
        return false;
    return series.images.every(isVideoImage);
}
